import {
  FarmRiskType,
  allowedDirectMonolineCurrentCarriers,
  combinedUMUIM,
  earthquakeStates,
  floodStates
} from '@ourbranch/lookups';
import { policyTypes } from '@ourbranch/policy-types';
import differenceInCalendarYears from 'date-fns/differenceInCalendarYears';
import cloneDeep from 'lodash-es/cloneDeep';
import isEmpty from 'lodash-es/isEmpty';
import omit from 'lodash-es/omit';
import pick from 'lodash-es/pick';
import mapValues from 'lodash-es/mapValues';
import isPlainObject from 'lodash-es/isPlainObject';
import { CondoType, condoTypesWithEntry, condoTypesWithUnitsInBuilding } from './constants';
import { awsDateFormatter } from './formatters';

// Helper function to recursively remove __typename from objects and nested objects
const cleanInput = (input) => {
  return mapValues(omit(input, ['__typename']), (val) => {
    if (isPlainObject(val)) {
      return cleanInput(omit(val, ['__typename']));
    }
    return val;
  });
};

// map for when we want to resubmit a quote from the offer page
export const mapOfferInputToQuoteRequestInput = (data) => {
  const quote = {
    address: data.correctedAddress.address.trim(),
    city: data.correctedAddress.city.trim(),
    state: data.correctedAddress.state,
    zip: data.correctedAddress.zip.trim(),
    rep: data.rep,
    affinity: data.global.affinity,
    affinityGroupStem: data.global.affinityGroupStem,
    firstName: data.fname.trim(),
    lastName: data.lname.trim(),
    isApartment: !!data.correctedAddress.unit && data.selectedOption.includes('R'),
    unit: data.correctedAddress.unit ? data.correctedAddress.unit.trim() : undefined,
    email: data.email ? data.email.trim() : undefined,
    phone: data.phone ? data.phone.trim() : undefined,
    additionalPhoneNumbers: data.additionalPhoneNumbers ? data.additionalPhoneNumbers : [],
    dateOfBirth: awsDateFormatter(data.drivers.find((d) => d.isPrimary).dateOfBirth),
    leadSource: data.leadSource,
    currentAutoCarrier: data.global.currentAutoCarrier ? data.global.currentAutoCarrier.trim() : null,
    currentAutoLimitBIPD: data?.autoCoverage?.policyLimitBIPD || null,
    continuousAutoCoverageYears: data.global.continuousAutoCoverageStartDate
      ? differenceInCalendarYears(new Date(), Date.parse(data.global.continuousAutoCoverageStartDate))
      : 0,
    driversLicenseState: data.drivers.find((d) => d.isPrimary).driversLicenseState,
    driversLicenseNumber: data.drivers.find((d) => d.isPrimary).driversLicenseNumber.trim(),
    // insuranceInPast31Days: !!data.global.currentlyAutoInsured,    mostRecentAutoInsuranceCoverageEndDate
    connectedHome: data.connectedHome,
    global: omit(data.global, [
      'currentAutoAMBEST',
      'hadInforceCVPolicy',
      'isRC1PriorAutoCoverageDefaultsSet',
      'priorCars',
      'priorDrivers',
      'rateControlDate',
      'receivedCVData',
      '__typename'
    ]),
    fromStaff: true,
    policyType: data.selectedOption,
    forcedMultiPolicyDiscount: data.forcedMultiPolicyDiscount,
    optIntoMarketingCampaigns: data.optIntoMarketingCampaigns
  };
  if (data.priorAddress?.address) {
    quote.priorAddress = omit(data.priorAddress, '__typename');
  }
  if (data.home) {
    quote.home = pick(data.home, [
      'basementSqFt',
      'basementType',
      'constructionType',
      'exteriorWallType',
      'garageCarCapacity',
      'garageType',
      'homeQuality',
      'homeLocation',
      'mortgageDetails',
      'numFamiliesInDwelling',
      'numFireplaces',
      'numFullBathrooms',
      'numHalfBathrooms',
      'numOccupants',
      'numStories',
      'numWoodBurningStoves',
      'ownershipStatus',
      'purchaseDate',
      'purchaseAmount',
      'roofOriginal',
      'roofShape',
      'roofType',
      'roofYear',
      'roofYearChangedManually',
      'roofDeck',
      'roofCover',
      'roofDeckAttachment',
      'roofToWallConnection',
      'openingProtection',
      'roofSWR',
      'sqFt',
      'swimmingPool',
      'typeOfHome',
      'yearBuilt',
      'fireHydrantWithin1000ft',
      'stormShutters',
      'fortifiedHome',
      'windstormMitigationLevel',
      'latitude',
      'longitude'
    ]);
    if (quote.home?.mortgageDetails?.length) {
      quote.home.mortgageDetails = quote.home.mortgageDetails.map((m) => ({
        ...omit(m, '__typename'),
        mortgageHolderAddress: isEmpty(m.mortgageHolderAddress) ? null : omit(m.mortgageHolderAddress, '__typename')
      }));
    }
    if (quote.home.homeLocation) {
      quote.home.homeLocation = omit(quote.home.homeLocation, '__typename');
    }
  }
  if (data.condo) {
    quote.condo = pick(data.condo, [
      'constructionType',
      'condoLocation',
      'hasUnitNumber',
      'condoQuality',
      'mortgageDetails',
      'numFullBathrooms',
      'numHalfBathrooms',
      'numOccupants',
      'ownershipStatus',
      'purchaseDate',
      'purchaseAmount',
      'roofShape',
      'sqFt',
      'typeOfCondo',
      'typeOfEntry',
      'hasBasement',
      'buildingHasMoreThanFiveUnits',
      'yearBuilt',
      'latitude',
      'longitude',
      'county',
      'fireHydrantWithin1000ft',
      'windstormMitigationLevel',
      'floor',
      'weeksRentedOut',
      'canineFactor',
      'territory',
      'numVehicles'
    ]);
    quote.condo.hasUnitNumber = !data.noUnitNumber;
    quote.condo.weeksRentedOut = data.condo.monthsRentedOut ? data.condo.monthsRentedOut * 4 : 0;
    if (!condoTypesWithEntry.includes(data.condo.typeOfCondo)) {
      quote.condo.typeOfEntry = null;
    }
    if (!condoTypesWithUnitsInBuilding.includes(quote.condo.typeOfCondo)) {
      quote.condo.buildingHasMoreThanFiveUnits = quote.condo.typeOfCondo !== CondoType.DETACHED;
    }
    if (quote.condo?.mortgageDetails?.length) {
      quote.condo.mortgageDetails = quote.condo.mortgageDetails.map((m) => ({
        ...omit(m, '__typename'),
        mortgageHolderAddress: isEmpty(m.mortgageHolderAddress) ? null : omit(m.mortgageHolderAddress, '__typename')
      }));
    }
  }
  if (data?.VIN) {
    quote.VIN = data?.VIN.trim();
  }
  if (quote.condoLocation) {
    quote.condoLocation = omit(quote.condoLocation, '__typename');
  }
  return quote;
};

export const mapRequestQuoteToInputType = (data) => {
  const quote = {
    address: data.address.trim(),
    city: data.city.trim(),
    state: data.state,
    zip: data.zip.trim(),
    rep: data.rep,
    affinity: data.affinity,
    affinityGroupStem: data.affinityGroupStem,
    encryptedData: data.encryptedData,
    fname: data.firstName.trim(),
    lname: data.lastName.trim(),
    isApartment: (data.isApartment || !!data.unit) && data.policyType.includes('R'),
    unit: data.unit ? data.unit.trim() : undefined,
    email: data.email ? data.email.trim() : undefined,
    phone: data.phone ? data.phone.trim() : undefined,
    additionalPhoneNumbers: data.additionalPhoneNumbers ? data.additionalPhoneNumbers : [],
    dateOfBirth: data.dateOfBirth ? awsDateFormatter(data.dateOfBirth) : null,
    leadSource: data.leadSource,
    currentAutoCarrier: data.currentAutoCarrier ? data.currentAutoCarrier.trim() : null,
    currentAutoLimitBIPD: data.policyLimitBIPD || null,
    continuousAutoCoverageYears:
      data.priorCoverageInYears || data.priorCoverageInYears === 0
        ? Number.parseInt(data.priorCoverageInYears, 10)
        : null,
    driversLicenseState: data.driversLicenseState ? data.driversLicenseState : null,
    driversLicenseNumber: data.driversLicenseNumber ? data.driversLicenseNumber.trim() : null,
    insuranceInPast31Days: !!data.insuranceInPast31Days,
    connectedHome: data.connectedHome,
    global: data.global,
    fromStaff: true,
    policyType: data.policyType,
    forcedMultiPolicyDiscount: data.forcedMultiPolicyDiscount,
    optIntoMarketingCampaigns: data.optIntoMarketingCampaigns,
    entity: data.entity || undefined
  };
  if (data.priorAddress?.address) {
    quote.priorAddress = omit(data.priorAddress, '__typename');
  }
  if (data.overrides) {
    quote.overrides = omit(data.overrides, '__typename');
  }
  if (data.policyType.includes('H') && data.home) {
    quote.home = pick(data.home, [
      'basementSqFt',
      'basementType',
      'constructionType',
      'exteriorWallType',
      'garageCarCapacity',
      'garageType',
      'homeQuality',
      'homeLocation',
      'mortgageDetails',
      'numFamiliesInDwelling',
      'numFireplaces',
      'numFullBathrooms',
      'numHalfBathrooms',
      'numOccupants',
      'numStories',
      'numWoodBurningStoves',
      'ownershipStatus',
      'purchaseDate',
      'purchaseAmount',
      'roofOriginal',
      'roofShape',
      'roofType',
      'roofYear',
      'roofYearChangedManually',
      'roofDeck',
      'roofCover',
      'roofDeckAttachment',
      'roofToWallConnection',
      'openingProtection',
      'roofSWR',
      'sqFt',
      'swimmingPool',
      'typeOfHome',
      'yearBuilt',
      'fireHydrantWithin1000ft',
      'stormShutters',
      'fortifiedHome',
      'windstormMitigationLevel',
      'latitude',
      'longitude'
    ]);
    if (quote.home?.mortgageDetails?.length) {
      quote.home.mortgageDetails = quote.home.mortgageDetails.map((m) => ({
        ...omit(m, '__typename'),
        mortgageHolderAddress: isEmpty(m.mortgageHolderAddress) ? null : omit(m.mortgageHolderAddress, '__typename')
      }));
    }
    if (quote.home.homeLocation) {
      quote.home.homeLocation = omit(quote.home.homeLocation, '__typename');
    }
  }
  if (data.policyType.includes('C') && data.condo) {
    quote.condo = pick(data.condo, [
      'constructionType',
      'condoLocation',
      'hasUnitNumber',
      'condoQuality',
      'mortgageDetails',
      'numFullBathrooms',
      'numHalfBathrooms',
      'numOccupants',
      'ownershipStatus',
      'purchaseDate',
      'purchaseAmount',
      'roofShape',
      'sqFt',
      'typeOfCondo',
      'typeOfEntry',
      'hasBasement',
      'buildingHasMoreThanFiveUnits',
      'yearBuilt',
      'latitude',
      'longitude',
      'county',
      'fireHydrantWithin1000ft',
      'windstormMitigationLevel',
      'floor',
      'weeksRentedOut',
      'canineFactor',
      'territory',
      'numVehicles'
    ]);
    quote.condo.hasUnitNumber = !data.noUnitNumber;
    quote.condo.weeksRentedOut = data.condo.monthsRentedOut ? data.condo.monthsRentedOut * 4 : 0;
    if (!condoTypesWithEntry.includes(data.condo.typeOfCondo)) {
      quote.condo.typeOfEntry = null;
    }
    if (!condoTypesWithUnitsInBuilding.includes(quote.condo.typeOfCondo)) {
      quote.condo.buildingHasMoreThanFiveUnits = quote.condo.typeOfCondo !== CondoType.DETACHED;
    }
    if (quote.condo?.mortgageDetails?.length) {
      quote.condo.mortgageDetails = quote.condo.mortgageDetails.map((m) => ({
        ...omit(m, '__typename'),
        mortgageHolderAddress: isEmpty(m.mortgageHolderAddress) ? null : omit(m.mortgageHolderAddress, '__typename')
      }));
    }
  }
  if (data.policyType.includes('F') && data.farmLocations) {
    quote.entity = cleanInput(data.entity);
    quote.farmLocations = data.farmLocations.map((location) => {
      const processedLocation = {
        description: location.description,
        numberOfAcres: location.numberOfAcres,
        address: location.address ? omit(location.address, '__typename') : undefined,
        legalDescription: location.legalDescription ? omit(location.legalDescription, '__typename') : undefined
      };

      // Don't send the coordinates if they are unknown
      if (processedLocation.legalDescription) {
        if (processedLocation.legalDescription.townshipCoordinates === 'unknown') {
          processedLocation.legalDescription.townshipCoordinates = undefined;
        }
        if (processedLocation.legalDescription.rangeCoordinates === 'unknown') {
          processedLocation.legalDescription.rangeCoordinates = undefined;
        }
      }

      // Process risks for this location
      if (location.risks && location.risks.length > 0) {
        processedLocation.risks = location.risks
          .map((risk) =>
            risk
              ? {
                  id: risk.id,
                  type: risk.type,
                  description: risk.description,
                  adjustmentFactor: risk.adjustmentFactor,
                  building:
                    risk.building && ![FarmRiskType.Fo6Scheduled, FarmRiskType.Fo6Unscheduled].includes(risk.type)
                      ? cleanInput(risk.building)
                      : undefined,
                  connectedHome: risk.connectedHome ? cleanInput(risk.connectedHome) : undefined,
                  coverages: risk.coverages ? cleanInput(risk.coverages) : undefined
                }
              : null
          )
          .filter((risk) => risk !== null);
      }

      return processedLocation;
    });
  }
  if (data.VIN) {
    quote.VIN = data.VIN.trim();
  }
  if (quote.condoLocation) {
    quote.condoLocation = omit(quote.condoLocation, '__typename');
  }
  return quote;
};

export const mapRevisedQuinstreetQuoteDetails = (quote) => {
  const revisedQuote = omit(mapRevisedQuoteDetails(quote), [
    'home.homeLocation',
    'homeCoverage.minimumHurricaneDeductible',
    'homeCoverage.minimumHurricaneDeductibleValue'
  ]);

  return {
    ...pick(revisedQuote, [
      'fname',
      'lname',
      'email',
      'includeUmbrella',
      'includeRenters',
      'isHomeOwner',
      'leadSource',
      'rep',
      'selectedOption',
      'noBindHome',
      'noBindAuto',
      'fromStaff',
      'includeEarthquake',
      'includeFlood',
      'getMVRs',
      'monthsAtCurrentAddress'
    ]),
    priorAddress: revisedQuote.priorAddress ? omit(revisedQuote.priorAddress, ['__typename']) : undefined,
    correctedAddress: revisedQuote.correctedAddress ? omit(revisedQuote.correctedAddress, ['__typename']) : undefined,
    auto: revisedQuote.auto,
    drivers: quote.drivers
      ? quote.drivers.map((driver) => ({
          ...pick(driver, [
            'id',
            'isPrimary',
            'isCoApplicant',
            'firstName',
            'lastName',
            'suffix',
            'dateOfBirth',
            'gender',
            'excludeDriver',
            'maritalStatus',
            'highestEducation',
            'driversLicenseState',
            'driversLicenseNumber',
            'ageFirstLicensed',
            'fullTimeStudent',
            'goodStudent',
            'teenDiscount',
            'courseDiscountCOnditionCheck',
            'takenCourse',
            'schoolName',
            'communityDriveParticipation',
            'waivedPIPWL',
            'pipElgible',
            'pipOptOut',
            'pipHasQHC',
            'assignedCar',
            'gotDrivingRecord',
            'clearUDRViolation',
            'needSR22',
            'autoViolationDates'
          ])
        }))
      : undefined,
    home: omit(revisedQuote.home, ['originalBasementSqFt', 'originalSqFt', 'roofReplacementYearMax', 'capeConditions']),
    homeCoverage: revisedQuote.homeCoverage,
    autoCoverage: omit(revisedQuote.autoCoverage, ['skipPDValidation']),
    existingCustomer: revisedQuote.existingCustomer,
    inviter: revisedQuote.inviter,
    connectedHome: revisedQuote.connectedHome,
    earthquakeCoverage: revisedQuote.earthquakeCoverage,
    floodCoverage: revisedQuote.floodCoverage,
    umbrellaCoverage: revisedQuote.umbrellaCoverage,
    trailers: revisedQuote.traileers,
    cars: quote.cars
      ? quote.cars.map((car) => ({
          ...pick(car, [
            'make',
            'model',
            'year',
            'VIN',
            'primaryUse',
            'deductibleComprehensive',
            'deductibleCollision',
            'limitRental',
            'roadsideAssistance',
            'limitACPE'
          ])
        }))
      : undefined,
    global: {
      ...pick(revisedQuote.global, [
        'autoEffectiveDate',
        'homeEffectiveDate',
        'currentlyHomeInsured',
        'manualInventorySubmission',
        'discountInventoryScore',
        'discountInventoryScoreAddedDate',
        'discountDriverScore',
        'discountPaperless',
        'homeownersPaymentType',
        'homeownersPaymentMethod',
        'autoPaymentType',
        'autoPaymentMethod',
        'currentlyAutoInsured',
        'currentAutoCarrier',
        'currentAutoCarrierStartDate',
        'currentAutoCarrierEndDate',
        'currentAutoCarrierPolicyNumber',
        'continuousAutoCoverageStartDate',
        'mostRecentAutoInsuranceCoverageEndDate',
        'priorIndividualBILimit',
        'priorOccurrenceBILimit',
        'breakupWithAuto',
        'currentHomeownersCarrier',
        'currentHomeownersCarrierPolicyNumber',
        'breakupWithHomeowners',
        'personalPropertyProtection',
        'emails',
        'phoneNumbers',
        'mailingAddress',
        'billingDayOfMonth',
        'autoBillingDayOfMonth',
        'homeBillingDayOfMonth',
        'affinity',
        'affinityGroupStem',
        'homeSecurityPartnerCustomerType',
        'employeeDiscount',
        'priorCancelNonPay'
      ])
    }
  };
};

const cleanMortgageDetails = (dwelling) => {
  if (dwelling && Array.isArray(dwelling.mortgageDetails)) {
    dwelling.mortgageDetails = dwelling.mortgageDetails.map((m) =>
      omit(m, ['__typename', 'county', 'mortgageHolderAddress.__typename'])
    );
  }
};

const removeTypename = (data) => {
  if (Array.isArray(data)) {
    return data.map(removeTypename);
  }
  if (data !== null && typeof data === 'object') {
    const cleaned = {};
    for (const [key, value] of Object.entries(data)) {
      if (key !== '__typename') {
        cleaned[key] = removeTypename(value);
      }
    }
    return cleaned;
  }
  return data;
};

export const mapRevisedQuoteDetails = (variables) => {
  const {
    cars,
    trailers,
    drivers,
    nonDrivers,
    removedDrivers,
    home,
    homeCoverage,
    autoCoverage,
    auto,
    rentersCoverage,
    umbrellaCoverage,
    condo,
    condoCoverage,
    global,
    priorAddress,
    includeUmbrella,
    includeRenters,
    includeEarthquake,
    includeFlood,
    earthquakeCoverage,
    floodCoverage,
    isHomeOwner,
    scheduledPersonalProperty,
    leadSource,
    selectedOption,
    existingCustomer,
    fname,
    lname,
    connectedHome,
    fromStaff,
    rep,
    getMVRs,
    inviter,
    email,
    correctedAddress,
    preBindUWCheck,
    overrides,
    branchPlusPolicies,
    forcedMultiPolicyDiscount,
    attachedHomeowners,
    attachedAuto,
    attachedRenters,
    attachedCondo,
    interestedParties,
    lienHolders,
    monthsAtCurrentAddress,
    farmLocations,
    farm,
    farmCoverage,
    entity
  } = variables;

  // clone to clean mortgageDetails
  const homeCopy = cloneDeep(home);
  if (homeCopy) {
    cleanMortgageDetails(homeCopy);
  }
  if (condo) {
    cleanMortgageDetails(condo);
  }
  if (farm) {
    cleanMortgageDetails(farm);
  }

  const isNonRentersBundle = ['HA', 'HB', 'AB', 'CA'].includes(selectedOption);

  return {
    fname,
    lname,
    email,
    selectedOption,
    fromStaff,
    getMVRs,
    noBindAuto: selectedOption === 'HB',
    noBindHome: selectedOption === 'AB',
    existingCustomer: existingCustomer ? omit(existingCustomer, ['__typename']) : null,
    includeUmbrella: includeUmbrella && isNonRentersBundle,
    includeRenters,
    includeEarthquake,
    includeFlood,
    earthquakeCoverage: earthquakeCoverage ? omit(earthquakeCoverage, ['__typename']) : undefined,
    floodCoverage: floodCoverage ? omit(floodCoverage, ['__typename']) : undefined,
    isHomeOwner,
    leadSource,
    rep,
    forcedMultiPolicyDiscount,
    attachedHomeowners,
    attachedAuto,
    attachedRenters,
    attachedCondo,
    monthsAtCurrentAddress,
    inviter: inviter ? omit(inviter, '__typename') : undefined,
    priorAddress: priorAddress ? omit(priorAddress, '__typename') : undefined,
    connectedHome: connectedHome ? omit(connectedHome, ['__typename']) : null,
    scheduledPersonalProperty:
      scheduledPersonalProperty?.items?.length && scheduledPersonalProperty?.deductible
        ? omit(scheduledPersonalProperty, ['__typename'])
        : null,
    home: homeCopy
      ? omit(homeCopy, [
          'county',
          'replacementCostEstimate',
          'highestEducation',
          'fpc',
          'wildfireHazardScore',
          'wildfireGallagherScore',
          'dtoName',
          'dtbowName',
          'dtbowLabel',
          'originalBasementSqFt',
          '__typename'
        ])
      : undefined,
    homeCoverage: homeCoverage ? omit(homeCoverage, ['__typename']) : undefined,
    auto: auto ? omit(auto, ['__typename']) : undefined,
    autoCoverage: autoCoverage ? omit(autoCoverage, ['__typename']) : undefined,
    condo: condo
      ? omit(condo, [
          'county',
          'replacementCostEstimate',
          'highestEducation',
          'fpc',
          'wildfireHazardScore',
          'wildfireGallagherScore',
          'dtoName',
          'dtbowName',
          'dtbowLabel',
          '__typename'
        ])
      : undefined,
    condoCoverage: condoCoverage ? omit(condoCoverage, ['__typename']) : undefined,
    rentersCoverage: rentersCoverage ? omit(rentersCoverage, ['__typename', 'rentersLocation.__typename']) : undefined,
    entity: entity ? removeTypename(entity) : undefined,
    farmLocations: farmLocations ? removeTypename(farmLocations) : undefined,
    farm: farm ? omit(farm, ['__typename']) : undefined,
    farmCoverage: farmCoverage ? omit(farmCoverage, ['__typename']) : undefined,
    cars: cars
      ? cars.map((car) =>
          omit(car, ['__typename', 'bodyType', 'year', 'model', 'make', 'garageLocation.__typename', 'addDate'])
        )
      : undefined,
    trailers: trailers
      ? trailers.map((trailer) =>
          omit(trailer, ['__typename', 'bodyType', 'model', 'make', 'garageLocation.__typename', 'addDate'])
        )
      : undefined,
    drivers: drivers
      ? drivers.map((driver) =>
          omit(driver, [
            '__typename',
            'firstName',
            'lastName',
            'middleName',
            'suffix',
            'age',
            'schoolLocation.__typename',
            'schoolZip',
            'autoViolationsForUW',
            'autoViolations',
            'autoViolationDates',
            'addDate',
            'ratableIncidentCount'
          ])
        )
      : undefined,
    removedDrivers: removedDrivers || undefined,
    nonDrivers: nonDrivers ? nonDrivers.map((p) => omit(p, ['__typename'])) : undefined,
    umbrellaCoverage: umbrellaCoverage
      ? omit(
          {
            ...umbrellaCoverage,
            rentalPropertyAddresses: umbrellaCoverage?.rentalPropertyAddresses
              ? umbrellaCoverage.rentalPropertyAddresses.map((x) => omit(x, ['__typename']))
              : null,
            otherPropertyAddresses: umbrellaCoverage?.otherPropertyAddresses
              ? umbrellaCoverage.otherPropertyAddresses.map((x) => omit(x, ['__typename']))
              : null
          },
          ['__typename']
        )
      : undefined,
    global: global
      ? {
          ...omit(global, [
            '__typename',
            'receivedCVData',
            'hadInforceCVPolicy',
            'rateControlDate',
            'priorDrivers',
            'priorCars',
            'preBindUWCheck',
            'isRC1PriorAutoCoverageDefaultsSet',
            'skipUnderwritingUntil' // Exclude from general mutations - use dedicated mutation instead
          ])
        }
      : undefined,
    correctedAddress,
    preBindUWCheck: preBindUWCheck
      ? {
          ...omit(preBindUWCheck, ['__typename']),
          homeVerification: preBindUWCheck.homeVerification
            ? omit(preBindUWCheck.homeVerification, ['__typename'])
            : undefined,
          autoVerification: preBindUWCheck.autoVerification
            ? omit(preBindUWCheck.autoVerification, ['__typename'])
            : undefined,
          farmVerification: preBindUWCheck.farmVerification
            ? omit(preBindUWCheck.farmVerification, ['__typename'])
            : undefined
        }
      : undefined,
    overrides: omit(overrides, ['__typename']),
    branchPlusPolicies: branchPlusPolicies
      ? branchPlusPolicies.map((policy) => omit(policy, ['__typename']))
      : undefined,
    interestedParties: interestedParties ? interestedParties.map((party) => omit(party, ['__typename'])) : undefined,
    lienHolders: lienHolders ? lienHolders.map((lien) => omit(lien, ['__typename'])) : undefined
  };
};

export const hasRejection = ({ rejections }) => rejections?.length > 0;

export const formattedError = (err) => {
  const lambdaUnhandledMessage = '{}';
  const message = err?.graphQLErrors?.[0]?.message || err?.message;
  try {
    if (!message) {
      const match = err.match(/^(?<message>.*?)\[(?<code>[0-9]+?)\](\[(?<metadata>.*?)\])?/);
      if (match?.groups) {
        return {
          message: match.groups.message,
          code: match.groups.code,
          metadata: match.groups.metadata
        };
      }
      return { message: err, code: 0 };
    }
    if (message === lambdaUnhandledMessage) {
      return { message: 'Unhandled Lambda Error', code: 0 };
    }

    // a json stringified error
    const parsed = JSON.parse(message);
    if (parsed) {
      if (parsed.message?.includes('Stripe')) {
        return {
          code: parsed.jse_cause?.code || 0,
          message: `Error from Stripe: ${parsed.message}`
        };
      }
      if (parsed.type?.includes('Stripe')) {
        return {
          code: 0,
          message: `Error from Stripe: ${parsed.raw?.message || ''} ${parsed.raw?.decline_code || ''}`
        };
      }
      if (!parsed.code && parsed.errorMessage) {
        const parsedMessage = JSON.parse(parsed.errorMessage);
        return parsedMessage;
      }
      return parsed;
    }

    // return raw message if cannot be parsed
    return { message, code: 0 };
  } catch {
    if (message) {
      return { message, code: 0 };
    }
    return { message: err, code: 0 };
  }
};
export const checkIfUnauthorizedAndPushToSearchPage = (error, history) => {
  if (error.message.includes('Not Authorized')) {
    console.log(error.message, 'pushing back to home');
    history.push('/search/offers');
  }
};

export const hasCoverageEligible = (coverage) => {
  if (coverage.length > 1) {
    return Number(coverage[0]) >= 250 && Number(coverage[1]) >= 500;
  }
  // CSL
  return Number(coverage[0]) >= 500;
};

export const canHaveExcessUM = ({ policyLimitUMBI, policyLimitUIMBI, state }) => {
  if (!policyLimitUMBI || !policyLimitUIMBI) {
    return false;
  }
  // must be 250/500 or 500CSL & in bix state

  const limitUMBI = policyLimitUMBI.includes('/') ? policyLimitUMBI.split('/') : policyLimitUMBI.split(' ').slice(0, 1);
  const limitUIMBI = policyLimitUIMBI.includes('/')
    ? policyLimitUIMBI.split('/')
    : policyLimitUIMBI.split(' ').slice(0, 1);
  return combinedUMUIM[state]
    ? hasCoverageEligible(limitUMBI)
    : hasCoverageEligible(limitUMBI) && hasCoverageEligible(limitUIMBI);
};

export const canAddUmbrellaUMUIM = ({ policyLimitUMBI, policyLimitUIMBI, state }) => {
  if (state === 'NH') {
    // Everything all the time in New Hampshire!
    return true;
  }
  return canHaveExcessUM({ policyLimitUMBI, policyLimitUIMBI, state });
};

export const canAddUmbrella = ({
  policyLimitBIPD = '/',
  coverageX,
  drivers,
  global,
  renewalVariables,
  includeUmbrella
}) => {
  // allow override from renewalVariables:
  if (global?.allowUmbrella || renewalVariables?.allowUmbrella) {
    return true;
  }

  // if we don't have a policy limit or coverageX, we can't add umbrella
  if (policyLimitBIPD === '/' || !coverageX) {
    return false;
  }
  const isCombinedSingleLimit = policyLimitBIPD.includes('CSL');
  const limitBI = isCombinedSingleLimit ? policyLimitBIPD.split(' ')[0] : policyLimitBIPD.split('/')[1];
  const cslMinimum = renewalVariables?.allow300KCSL ? 300 : 500;

  // Only disable umbrella because of prior carrier in production
  // also skip this check for kurt muldrew-clean (!== 'undefined')
  if (
    process.env.NODE_ENV !== 'development' &&
    global.currentAutoAMBEST &&
    global.currentAutoAMBEST !== 'undefined' &&
    !allowedDirectMonolineCurrentCarriers.includes(global.currentAutoAMBEST) &&
    global.currentAutoCarrier &&
    !renewalVariables?.allowUmbrella &&
    global.currentAutoCarrier !== 'BRANCH INSURANCE EXC' &&
    !includeUmbrella // if this policy already has umbrella, allow it here because this is a pre-bind check
  ) {
    return false;
  }

  return (
    Number.parseInt(limitBI, 10) >= cslMinimum &&
    coverageX >= 300000 &&
    (!drivers || drivers?.every((d) => !d.excludeDriver && !d.hasViolationsBlockingUmbrella))
  );
};

export const checkAdditionalCoverages = (offer, policyType) => {
  if (!offer || !policyType) {
    return { canAddEarthquake: false, canAddFlood: false };
  }
  const hasHome = policyType === policyTypes.Home || policyType === policyTypes.HABundle;
  const state = offer?.quote?.correctedAddress?.state;
  const canAddEarthquake = hasHome && earthquakeStates.includes(state);
  const canAddFlood = hasHome && floodStates.includes(state);
  return { canAddEarthquake, canAddFlood };
};
